<template>
    <div class="auction-list-item">
        <div class="auction-list-item__header">
            <div class="auction-list-item__header-left">
                <router-link
                    class="auction-list-item__number"
                    :to="{ name: 'ViewAuction', params: { auction_id: auction.id } }"
                >
                    Лот №{{ auction.id }}
                </router-link>

                <AuctionItemStatus
                    :status-text="getAuctionStatusLabel(auction.status)"
                    :status-class="auction.status"
                />
            </div>

            <div class="auction-list-item__header-right">
                <AuctionItemParticipationStatus
                    v-if="auction.user_joining && !auction.is_winner"
                    status-text="Вы участвуете"
                    status-class="participating"
                />

                <AuctionItemParticipationStatus
                    v-if="auction.is_winner"
                    status-text="Вы победитель!"
                    status-class="winner"
                />

                <UITimer
                    v-if="auction.status === AuctionStatus.InProgress"
                    :seconds-to-end="auctionSecondsToEnd"
                />

                <UIDropDown
                    v-if="canSendAnnouncementAboutAuction(auction)
                        || canForceCompleteAuction(auction)
                        || canProlongAuction(auction)
                        || canEditAuction(auction)
                        || canAnnulAuction(auction)
                    "
                    as-modal-on-mobile
                >
                    <template #elements>
                        <li
                            v-if="canSendAnnouncementAboutAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openSendAnnouncementAboutAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/send-announcement.svg" />
                            <span>Отправить анонс</span>
                        </li>

                        <router-link
                            v-if="canEditAuction(auction)"
                            :to="{ name: 'EditAuction', params: { auction_id: auction.id } }"
                            class="ui-dropdown-menu__element"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                            <span>Редактировать</span>
                        </router-link>

                        <li
                            v-if="canProlongAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openProlongAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/ic_round-more-time.svg" />
                            <span>Продлить</span>
                        </li>

                        <li
                            v-if="canForceCompleteAuction(auction)"
                            class="ui-dropdown-menu__element danger"
                            @click="openForceCompleteAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                            <span>Завершить</span>
                        </li>

                        <li
                            v-if="canAnnulAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openAnnulAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/annul.svg" />
                            <span>Аннулировать</span>
                        </li>
                    </template>
                </UIDropDown>
            </div>
        </div>

        <div class="auction-list-item__gallery">
            <PhotoGallery :images="auctionCarPhotos" />
        </div>

        <div class="auction-list-item__body">
            <div class="auction-list-item__body-left">
                <div class="auction-list-item-info">
                    <div class="auction-list-item-info__header">
                        <div class="auction-list-item-info__header-title">
                            <span>{{ getCarFullName(auction.car) }}</span>
                        </div>

                        <div class="auction-list-item-info__header-specs">
                            {{ getCarSpecification(auction.car) }}
                        </div>
                    </div>

                    <div v-if="auction.car.comment" class="auction-list-item-info__description">
                        {{ auction.car.comment }}
                    </div>

                    <CarTags v-if="auction.car.tags.length > 0" :tags="auction.car.tags" />
                </div>

                <CarItemDetails
                    :details="[
                        { title: 'VIN:', value: auction.car.vin },
                        { title: 'Тип поступления:', value: getCarAdmissionTypeLabel(auction.car.admission_type) },
                        { title: 'Тип НДС:', value: getCarNdsTypeLabel(auction.car.nds_type) },
                        { title: 'ПТС:', value: auction.car.pts_number },
                        { title: 'Дилер:', value: getCarDealerFullName(auction.car) },
                    ]"
                />
            </div>

            <div class="auction-list-item__body-right">
                <div class="auction-list-item__date">
                    <span class="auction-list-item__date-label">Дата начала</span>
                    <span class="auction-list-item__date-value">{{ formatFromUTC(auction.start_date) }}</span>
                </div>

                <div class="auction-list-item__date">
                    <span class="auction-list-item__date-label">Дата окончания</span>
                    <span class="auction-list-item__date-value">{{ formatFromUTC(auction.end_date) }}</span>
                </div>

                <div class="auction-list-item__date">
                    <span class="auction-list-item__date-label">Стартовая цена</span>
                    <span class="auction-list-item__date-value">{{ numberFormat(auction.start_price) }} ₽</span>
                </div>

                <router-link
                    v-if="auction.status !== AuctionStatus.InProgress"
                    :to="{ name: 'ViewAuction', params: { auction_id: auction.id } }"
                >
                    <UISimpleButton>
                        Подробнее
                    </UISimpleButton>
                </router-link>
            </div>
        </div>

        <div
            v-if="auction.status === AuctionStatus.InProgress"
            class="auction-list-item__footer"
        >
            <div
                v-if="!isAdmin() && !auction.is_user_lot"
                class="auction-list-item__footer-block"
                :class="{ 'auction-list-item__footer-block-user-bid': auctionLastUserBidValue }"
            >
                <div class="auction-list-item__footer-block-title">Ваша ставка</div>
                <div class="auction-list-item__footer-block-value">
                    {{ auctionLastUserBidValue ? numberFormat(auctionLastUserBidValue) + ' ₽' : '—' }}
                </div>
            </div>

            <div class="auction-list-item__footer-block">
                <div class="auction-list-item__footer-block-title">Текущая ставка</div>
                <div class="auction-list-item__footer-block-value">
                    {{ auctionLastBidValue ? numberFormat(auctionLastBidValue) + ' ₽' : '—' }}
                </div>
            </div>

            <div class="auction-list-item__footer-block">
                <div class="auction-list-item__footer-block-title">Всего ставок</div>
                <div class="auction-list-item__footer-block-value">
                    {{ auctionBidsCount }}
                </div>
            </div>

            <div v-if="isAdmin() || auction.is_user_lot"></div>

            <router-link
                v-if="canCreateBid(auction)"
                :to="{ name: 'ViewAuction', params: { auction_id: auction.id } }"
            >
                <UISimpleButton>
                    Сделать ставку
                </UISimpleButton>
            </router-link>

            <router-link v-else :to="{ name: 'ViewAuction', params: { auction_id: auction.id } }">
                <UISimpleButton>
                    Подробнее
                </UISimpleButton>
            </router-link>
        </div>

        <div
            v-if="[AuctionStatus.AwaitingResult, AuctionStatus.Completed].indexOf(auction.status) >= 0 && auction.winner"
        >
            <div
                v-if="auction.is_user_lot || isAdmin()"
                class="auction-list-item__footer"
            >
                <div class="auction-list-item__footer-block auction-list-item__footer-block-winner">
                    <div class="auction-list-item__footer-block-title">Победитель</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ auction.winner.dealer_name }}
                    </div>
                </div>

                <div class="auction-list-item__footer-block">
                    <div class="auction-list-item__footer-block-title">Ставка победителя</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ numberFormat(auction.winner.value) }} ₽
                    </div>
                </div>

                <div class="auction-list-item__footer-block">
                    <div class="auction-list-item__footer-block-title">Всего ставок</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ auctionBidsCount }}
                    </div>
                </div>

                <router-link
                    v-if="canConfirmAuctionWinner(auction)"
                    :to="{ name: 'ViewAuction', params: { auction_id: auction.id } }"
                >
                    <UISimpleButton>
                        Подтвердить победителя
                    </UISimpleButton>
                </router-link>
            </div>

            <div v-else class="auction-list-item__footer auction-completed__results">
                <div class="auction-list-item__footer-block" :class="{ 'auction-list-item__footer-block-user-bid': auctionLastUserBidValue }">
                    <div class="auction-list-item__footer-block-title">Ваша ставка</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ auctionLastUserBidValue ? numberFormat(auctionLastUserBidValue) + ' ₽' : '—' }}
                    </div>
                </div>

                <div class="auction-list-item__footer-block">
                    <div class="auction-list-item__footer-block-title">Ставка победителя</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ numberFormat(auction.winner.value) }} ₽
                    </div>
                </div>

                <div class="auction-list-item__footer-block">
                    <div class="auction-list-item__footer-block-title">Всего ставок</div>
                    <div class="auction-list-item__footer-block-value">
                        {{ auctionBidsCount }}
                    </div>
                </div>

                <div class="auction-list-item__footer-documents">
                    <AuctionSetDeliveryRequired
                        v-if="canSetDeliveryRequired(auction)"
                        :auction-id="auction.id"
                        :is-delivery-required="auction.delivery_required ?? false"
                    />

                    <UIDropDown
                        v-if="canViewAuctionSaleContract(auction) || canViewAuctionInvoice(auction)"
                        class="auction-detail-bid__action"
                    >
                        <template #trigger>
                            <UISimpleButton>
                                <template #icon>
                                    <img v-svg-inline src="@/assets/icons/report.svg" />
                                </template>

                                <template #icon-right>
                                    <img v-svg-inline src="@/assets/icons/ui/select-arrow.svg" />
                                </template>
                                Документы
                            </UISimpleButton>
                        </template>

                        <template #elements>
                            <li
                                v-if="canViewAuctionSaleContract(auction)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadSaleContract"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>ДКП</span>
                            </li>

                            <li
                                v-if="canViewAuctionInvoice(auction)"
                                class="ui-dropdown-menu__element"
                                @click="onDownloadInvoice"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/document.svg" />
                                <span>Счет на оплату</span>
                            </li>
                        </template>
                    </UIDropDown>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import { computed, h, defineEmits, defineProps } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalKey } from '@/injection/keys.ts';

    import { Auction, AuctionStatus, getAuctionStatusLabel } from '@/types/auction.type.ts';
    import { CarPhoto, getCarAdmissionTypeLabel, getCarNdsTypeLabel } from '@/types/car.type.ts';

    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import { getCarDealerFullName, getCarFullName, getCarSpecification } from '@/utils/car';
    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import PhotoGallery from '@/components/ui/PhotoGallery/PhotoGallery.vue';
    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UITimer from '@/components/ui/UITimer/UITimer.vue';

    import AuctionItemStatus from '@/components/auction/AuctionItemStatus/AuctionItemStatus.vue';
    import AuctionItemParticipationStatus from '@/components/auction/AuctionItemParticipationStatus/AuctionItemParticipationStatus.vue';
    import AuctionSetDeliveryRequired from '@/components/auction/AuctionSetDeliveryRequired/AuctionSetDeliveryRequired.vue';
    import CarItemDetails from '@/components/cars/CarItemDetails/CarItemDetails.vue';
    import CarTags from '@/components/cars/CarTags/CarTags.vue';
    import ContinueAuctionModal from '@/components/auction/ContinueAuctionModal/ContinueAuctionModal.vue';
    import AuctionConfirmSendAnnouncement from '@/components/auction/AuctionConfirmSendAnnouncement/AuctionConfirmSendAnnouncement.vue';
    import AuctionConfirmAnnul from '@/components/auction/AuctionConfirmAnnul/AuctionConfirmAnnul.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    const { auction } = defineProps<{
        auction: Auction,
    }>();

    const emit = defineEmits<{
        (e: 'updated'): void,
    }>();

    const $modal = injectStrict(ModalKey);

    const {
        isAdmin,
        canCreateBid,
        canForceCompleteAuction,
        canProlongAuction,
        canEditAuction,
        canConfirmAuctionWinner,
        canSetDeliveryRequired,
        canViewAuctionSaleContract,
        canViewAuctionInvoice,
    } = usePermission();
    const {
        forceCompleteAuction,
        prolongAuction,
        sendAnnouncementAboutAuction,
        annulAuction,
    } = useAuctionRepository();
    const { canSendAnnouncementAboutAuction, canAnnulAuction } = usePermission();
    const { downloadAuctionSaleContract, downloadAuctionInvoice } = useDocumentDownloader();
    const { showSuccess } = useNotifications();

    const defaultImages = [noPhotoImage, noPhotoImage, noPhotoImage, noPhotoImage, noPhotoImage];

    const auctionCarPhotos = computed(() => {
        return auction.car.photos !== undefined && auction.car.photos.length
            ? auction.car.photos.map((photo: CarPhoto) => photo.path_small)
            : defaultImages;
    });

    const auctionBidsCount = computed(() => {
        return auction.broadcastInfo?.bids_count ?? auction.bids_count;
    });

    const auctionLastBidValue = computed(() => {
        return auction.broadcastInfo?.last_bid?.value;
    });

    const auctionLastUserBidValue = computed(() => {
        return auction.user_bids && auction.user_bids.length > 0
            ? auction.user_bids[auction.user_bids.length - 1].value
            : undefined;
    });

    const auctionSecondsToEnd = computed(() => {
        return auction.broadcastInfo ? auction.broadcastInfo.seconds_to_end : 0;
    });

    function openSendAnnouncementAboutAuctionConfirmModal(): void {
        $modal?.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    auction ? h(AuctionConfirmSendAnnouncement, { auction: auction }) : '',
            },
            props: {
                onConfirm: async () => {
                    await sendAnnouncementAboutAuction(auction.id);

                    showSuccess({
                        title: 'Анонс отправлен',
                    });
                },
            },
        });
    }

    function openAnnulAuctionConfirmModal(): void {
        $modal?.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    auction ? h(AuctionConfirmAnnul, { auction: auction }) : '',
            },
            props: {
                onConfirm: async () => {
                    await annulAuction(auction.id);

                    showSuccess({
                        title: 'Аукцион аннулирован',
                    });

                    emit('updated');
                },
            },
        });
    }

    function openProlongAuctionConfirmModal(): void {
        $modal?.open({
            component: ContinueAuctionModal,
            props: {
                auction,
                onConfirmed: onProlongAuction,
            },
            modalType: 'narrow',
        });
    }

    function onProlongAuction(minutes: number): void {
        $modal?.close();
        prolongAuction(auction.id, minutes).then(() => {
            showSuccess({
                title: 'Аукцион продлен',
            });

            emit('updated');
        });
    }

    function openForceCompleteAuctionConfirmModal(): void {
        onForceCompleteAuction();
    }

    function onForceCompleteAuction(): void {
        forceCompleteAuction(auction.id).then(() => {
            showSuccess({
                title: 'Аукцион завершен',
            });

            emit('updated');
        });
    }

    function onDownloadSaleContract(): void {
        if (auction.id) {
            downloadAuctionSaleContract(auction.id, `auction-${auction.id}-sale-contract.docx`);
        }
    }

    function onDownloadInvoice(): void {
        if (auction.id) {
            downloadAuctionInvoice(auction.id, `auction-${auction.id}-invoice.xlsx`);
        }
    }
</script>

<style lang="scss">
    @use './AuctionListItem.scss' as *;
</style>
